import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import SessionForm from './SessionForm';
import { fetchSession, type SessionView } from './sessionApi';

type Load =
  | { state: 'loading' }
  | { state: 'ready'; view: SessionView }
  | { state: 'error' };

const Notice: React.FC<{ testId: string; title: string; body: string }> = ({ testId, title, body }) => (
  <div className="max-w-xl mx-auto text-center py-12" data-testid={testId}>
    <h2 className="text-xl font-bold text-custom-pink mb-3">{title}</h2>
    <p className="text-gray-600">{body}</p>
  </div>
);

const SessionCheckout: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [load, setLoad] = useState<Load>({ state: 'loading' });

  useEffect(() => {
    if (!sessionId) { setLoad({ state: 'error' }); return; }
    let cancelled = false;
    fetchSession(sessionId)
      .then((view) => { if (!cancelled) setLoad({ state: 'ready', view }); })
      .catch(() => { if (!cancelled) setLoad({ state: 'error' }); });
    return () => { cancelled = true; };
  }, [sessionId]);

  const handleExpire = useCallback(() => {
    setLoad((prev) =>
      prev.state === 'ready' ? { state: 'ready', view: { ...prev.view, status: 'expired' } } : prev,
    );
  }, []);

  if (load.state === 'loading') {
    return <div className="text-center py-12 text-gray-500">Завантаження…</div>;
  }

  if (load.state === 'error') {
    return (
      <Notice
        testId="session-error"
        title="Посилання недійсне"
        body="Не вдалося знайти це замовлення. Напишіть продавцю в Telegram."
      />
    );
  }

  const { view } = load;

  if (view.status === 'sold') {
    return <Notice testId="session-sold" title="Товар продано" body="На жаль, цей товар уже продано." />;
  }

  // A buyer who reloads after paying. Never a countdown, never "your hold
  // expired" — their money is already recorded and all we need is the receipt.
  if (view.status === 'awaiting_confirmation') {
    return (
      <Notice
        testId="session-awaiting-confirmation"
        title="Оплату зафіксовано"
        body="Надішліть, будь ласка, скріншот або квитанцію боту в Telegram — продавець підтвердить замовлення."
      />
    );
  }

  if (view.status === 'expired') {
    return (
      <Notice
        testId="session-expired"
        title="Час бронювання вийшов"
        body="Товар більше не зарезервовано. Натисніть КУПИТИ в каналі ще раз, якщо він досі доступний."
      />
    );
  }

  return (
    <SessionForm
      orderId={view.orderId}
      itemName={view.itemName}
      amount={view.amount}
      expiresAt={view.expiresAt}
      onExpire={handleExpire}
    />
  );
};

export default SessionCheckout;
